import {z} from "zod"
import {GraphNode} from "#graph/nodes/graph/node"
import type {GraphNodeDefinition} from "#graph/nodes/graph/types"
import type {NodeParameters} from "#graph/nodes/base/types"

export type Evaluated<T> = T extends GraphNode<infer Definition> ? z.infer<Definition["validation"]["returns"]> : T

export const isGraphNodeInstance = (value: unknown): value is GraphNode => {
    return value instanceof GraphNode
}

export const graphNodeSchema = (
    {definition, parameters}: {definition: z.ZodType; parameters: z.ZodType} = {definition: z.unknown(), parameters: z.unknown()},
): z.ZodType<GraphNode> =>
    z.any().superRefine((arg, ctx): arg is GraphNode => {
        if (arg instanceof GraphNode) {
            const matchesDefinition = !definition || definition.safeParse(arg.definition.validation.returns).success
            const matchesParameters = !parameters || parameters.safeParse(arg.definition.validation.parameters).success
            return matchesDefinition && matchesParameters
        } else {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Expected nodeGraph",
                fatal: true,
            })

            return z.NEVER
        }
    }) // We have to use .superRefine() because .refine() doesn't abort early https://github.com/colinhacks/zod#abort-early

export const ensureValidResult = <Definition extends GraphNodeDefinition, ReturnType>(graph: GraphNode<Definition>, value: ReturnType) => {
    const schema = graph.getReturnSchema()
    const result = schema.safeParse(value)
    if (!result.success) {
        throw new Error(`Invalid returned value for node ${graph.constructor.name}`, {
            cause: {value, issues: result.error.issues, schema},
        })
    } else return result.data as ReturnType
}

export const graphNodeReturning = <ReturnType>(schema: z.ZodType<ReturnType>, nodeType = GraphNode) => {
    return z.instanceof(nodeType).superRefine((graphNode, zodContext) => {
        // TODO: remove (currently no way to do this with zod v4 mini)
        // if (graphNode.returning(schema)) {
        //     return true
        // } else {
        //     zodContext.addIssue({
        //         code: z.ZodIssueCode.custom,
        //         message: `Node's return type schema does not match the expected schema`,
        //         params: {
        //             expected: schema,
        //             actual: graphNode.getReturnSchema(),
        //         },
        //     })
        //     return false
        // }
    })
}

export type EvaluatingTo<ValueType, Context = unknown> = z.ZodUnion<[z.ZodSchema<ValueType>, z.ZodType<GraphNodeReturning<ValueType, Context>>]>

export const evaluatingTo = <ValueType>(schema: z.ZodSchema<ValueType>, nodeType = GraphNode): EvaluatingTo<ValueType> => {
    return schema.or(graphNodeReturning(schema, nodeType))
}

export const asPrimitiveValue = <ValueType>(value: ValueType | GraphNodeReturning<ValueType>): ValueType | null => {
    return value instanceof GraphNode ? null : (value as ValueType)
}

export type GraphNodeReturning<ReturnType, Context = unknown> = GraphNode<
    {
        validation: {
            returns: z.ZodType<ReturnType>
            parameters: z.ZodType<NodeParameters>
        }
    },
    Context
>

export type GraphNodeWithParameters<ParameterType extends {[key: string]: z.ZodType}> = GraphNode<
    GraphNodeDefinition & {
        validation: {
            parameters: z.ZodObject<ParameterType>
        }
    }
>

export type ParameterValue<Value, Context = unknown> = Value | GraphNodeReturning<Value, Context>
